import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import React from "react";

interface Props<M extends LocationModel> {
    locations: M[];
    locationSearchFilter: string;
    locationsPagination?: PaginationModel;
    currentLocation?: google.maps.LatLng;
    selectedLocation?: M;
    showSelectedLocation: boolean;
    locationType?: "WAREHOUSE" | "LOCATION";
    setLocationOfInfoWindow: (location: M | undefined) => void;
    setCurrentLocationInfoWindow: (currentLocation: CurrentLocationInfoWindow | undefined) => void;
    hoveredIndex?: any;
}

export interface LocationInfoWindow<M extends LocationModel> {
    position: google.maps.LatLng;
    location: M;
}
export interface CurrentLocationInfoWindow {
    position: google.maps.LatLng;
}
export interface LocationGoogleMapsMarker {
    key: string;
    locationId?: string;
    position: google.maps.LatLng;
    type: LocationGoogleMapMarkerType;
    icon: string;
    onClick: () => void;
}

export type LocationGoogleMapMarkerType = "WAREHOUSE" | "LOCATION" | "SELECTED" | "CURRENT_LOCATION";

/**
 * Manages the Google Markers for the passed in locations and InfoWindows for a Google Map.
 * @param props
 */
const useLocationGoogleMarkers = <M extends LocationModel>({
    locations,
    locationSearchFilter,
    locationsPagination,
    currentLocation,
    selectedLocation,
    showSelectedLocation,
    locationType,
    setLocationOfInfoWindow,
    setCurrentLocationInfoWindow,
    hoveredIndex 
}: Props<M>) => {
    const [mapMarkersElements, setMapMarkersElements] = React.useState<LocationGoogleMapsMarker[]>([]);

    

    // const getMarkerIcon = (index: number, isHovered: boolean): string => {
    //     const svg = `
    //         <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
    //             <rect width="40" height="40" style="fill:${isHovered ? 'red' : 'blue'}"/>
    //             <text x="20" y="25" font-size="20" fill="white" text-anchor="middle" font-family="Arial">${index}</text>
    //         </svg>`;
    //     return `data:image/svg+xml;base64,${btoa(svg)}`;
    // };
    const getMarkerIcon = (index: number, isHovered: boolean): string => {
        const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                // <rect width="40" height="40" style="fill:${isHovered ? 'orange' : 'green'}"/>
                <text x="20" y="25" font-size="20" fill="white" text-anchor="middle" font-family="Arial">${index}</text>
            </svg>`;
        return `data:image/svg+xml;base64,${btoa(svg)}`;
    };


    const createMarkerClickHandler = (location: M) => {
        return () => {
            setCurrentLocationInfoWindow(undefined);
            setLocationOfInfoWindow(location);
        };
    };

    const createCurrentLocationMarkerClickHandler = () => {
        if (currentLocation) {
            setCurrentLocationInfoWindow({ position: currentLocation });
        }
        setLocationOfInfoWindow(undefined);
    };

    const getLocationNumber = (index: number): number => {
        if (!locationsPagination) {
            return 0;
        }
        const pageSize = locationsPagination.pageSize;
        const page = locationsPagination.page;
        return index + 1 + pageSize * (page - 1);
    };

    const clearLocationGoogleMarkers = () => {
        setMapMarkersElements([]);
        closeInfoWindows();
    };

    const closeInfoWindows = () => {
        setLocationOfInfoWindow(undefined);
        setCurrentLocationInfoWindow(undefined);
    };

    // From warehouses create Google Markers that can be shown on Google Map.
    React.useEffect(() => {
        const getBlueRectangleIconWithIndex = (index: number): string => {
            const svg = `
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                    <rect width="40" height="40" style="fill:blue"/>
                    <text x="20" y="25" font-size="20" fill="white" text-anchor="middle" font-family="Arial">${index}</text>
                </svg>`;
            return `data:image/svg+xml;base64,${btoa(svg)}`;
        };
        const blueRectangleIcon = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+PHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBzdHlsZT0iZmlsbDpibHVlIi8+PC9zdmc+";
        // const markers: LocationGoogleMapsMarker[] = locations.map((location, index) => ({
        //     key: location.id,
        //     locationId: location.id,
        //     position: new google.maps.LatLng(location.latitude, location.longitude),
        //     type: locationType || "LOCATION",
        //     icon:getBlueRectangleIconWithIndex(getLocationNumber(index)),
        //     // icon: `https://mt.google.com/vt/icon/text=${getLocationNumber(
        //     //     index,
        //     // )}&psize=16&color=ff330000&name=icons/spotlight/spotlight-waypoint-b.png&ax=44&ay=48&scale=1`,
        //     onClick: createMarkerClickHandler(location),
        // }));
        // Add Selected Location Marker

        const markers: LocationGoogleMapsMarker[] = locations.map((location, index) => {
            const isHovered = index === hoveredIndex;
            return {
                key: location.id,
                locationId: location.id,
                position: new google.maps.LatLng(location.latitude, location.longitude),
                type: locationType || "LOCATION",
                icon: getMarkerIcon(getLocationNumber(index), isHovered),
                onClick: createMarkerClickHandler(location),
            };
        });

        // if (showSelectedLocation && selectedLocation) {
        //     markers.push({
        //         key: "selected",
        //         position: new google.maps.LatLng(selectedLocation.latitude, selectedLocation.longitude),
        //         type: "SELECTED",
        //         // icon: "https://mt.google.com/vt/icon?name=icons/spotlight/spotlight-waypoint-blue.png",
        //         icon:blueRectangleIcon,
        //         onClick: createMarkerClickHandler(selectedLocation),
        //     });
        // }
        // Add Current/Search Location Marker

        if (showSelectedLocation && selectedLocation) {
            markers.push({
                key: "selected",
                position: new google.maps.LatLng(selectedLocation.latitude, selectedLocation.longitude),
                type: "SELECTED",
                icon: getMarkerIcon(getLocationNumber(locations.indexOf(selectedLocation)), false), // Default color for selected
                onClick: createMarkerClickHandler(selectedLocation),
            });
        }
        // if (currentLocation && currentLocation.lat() !== 0 && currentLocation.lng() !== 0) {
        //     markers.push({
        //         key: "current-location",
        //         position: currentLocation,
        //         type: "CURRENT_LOCATION",
        //         // icon: "https://www.gstatic.com/images/icons/material/system_gm/2x/place_gm_blue_24dp.png",
        //         icon:blueRectangleIcon,
        //         onClick: createCurrentLocationMarkerClickHandler,
        //     });
        // }
        if (currentLocation && currentLocation.lat() !== 0 && currentLocation.lng() !== 0) {
            markers.push({
                key: "current-location",
                position: currentLocation,
                type: "CURRENT_LOCATION",
                icon: getMarkerIcon(-1, false), // Default color for current location
                onClick: createCurrentLocationMarkerClickHandler,
            });
        }

        setMapMarkersElements(markers);
    }, [
        locations,
        currentLocation,
        locationSearchFilter,
        locationsPagination,
        selectedLocation,
        showSelectedLocation,
        locationType,
        hoveredIndex 
    ]);

    return {
        mapMarkersElements,
        clearLocationGoogleMarkers,
        closeInfoWindows,
        getLocationNumber,
       
    };
};

export default useLocationGoogleMarkers;
